<script lang="ts" setup>
import { useModal } from 'vue-final-modal'

import BasicButton from '~/components/form-elements/BasicButton.vue'
import TileMenu from '~/components/menus/TileMenu.vue'
import ReferralProgramInstructions from '~/components/modal-windows/supplier/ReferralProgramInstructions.vue'
import SupplierContent from '~/components/supplier/SupplierContent.vue'
import { useAccount } from '~/composables/account'
import constants from '~/constants'
import menuItems from '~/constants/menu-items'
import AuthScope from '~/enums/auth-scope'
import Page from '~/enums/page'
import { useAuthStore } from '~/store/auth'
import { useSupplierFaqsStore } from '~/store/supplier-faqs'
import { useSuppliersStore } from '~/store/suppliers'
import { useUserStore } from '~/store/user'

const usefulLinks = [
  { name: 'Оферта для поставщиков', url: constants.documentUrls.offerForSuppliers },
  { name: 'Матрица маркетинговых инструментов', url: constants.documentUrls.marketingTools },
  { name: 'Маркетинговые кейсы', url: constants.documentUrls.marketingCases },
  { name: 'Заявка на поставку', url: constants.documentUrls.supplyRequest },
  { name: 'Транспортный лист', url: constants.documentUrls.shippingList }
]

const router = useRouter()
const { openFeedbackModalWindow, signOut } = useAccount()
const authStore = useAuthStore()
const supplierFaqsStore = useSupplierFaqsStore()
const { getSupplierFaqs } = storeToRefs(supplierFaqsStore)
const suppliersStore = useSuppliersStore()
const userStore = useUserStore()

const title = computed(() => userStore.isSupplier ? 'ЛК поставщика' : 'Поставщикам')

useHead({
  title: `${title.value} — iTAB`,
  meta: [{
    name: 'description',
    content: 'Компания iTAB заинтересована в надёжных и открытых к сотрудничеству партнёрах по поставкам товаров.'
  }]
})

async function openReferralProgramInstructionsModalWindow (): Promise<void> {
  await useModal({ component: ReferralProgramInstructions }).open()
}

onMounted(() => {
  watchEffect(async () => {
    if (userStore.roles.length === 0) {
      return
    }

    if (userStore.isSupplier) {
      await suppliersStore.fetchOOS()
      await supplierFaqsStore.fetchSupplierFaqs(AuthScope.Supplier)
    }
  })
})
</script>

<template>
  <SupplierContent is-main-page :title="title">
    <Heading :title="title">
      <template #buttons>
        <BasicButton color="body-background" is-small title="ЛК клиента" @click="navigateTo(Page.Account)" />

        <BasicButton
          v-if="userStore.isSpecialist"
          color="body-background"
          is-small
          title="ЛК специалиста"
          @click="navigateTo(Page.Specialist)"
        />

        <BasicButton
          v-if="userStore.hasAccessToAdminPanel"
          color="body-background"
          is-small
          title="Админпанель"
          @click="navigateTo(Page.Admin)"
        />
      </template>
    </Heading>

    <TileMenu class="mb-6" :items="menuItems.supplier" />

    <Notification
      v-if="suppliersStore.percOfOSS"
      class="mb-6"
      :status="suppliersStore.percOfOSS > 10 ? 'warning' : 'success'"
    >
      <template v-if="suppliersStore.percOfOSS <= 10">
        Процент аутофстока по вашим брендам&nbsp;составляет {{ suppliersStore.percOfOSS }}%
      </template>
      <template v-else>
        Обратите внимание! Процент аутофстока по&nbsp;вашим брендам выше, чем у&nbsp;30% других брендов каталога. Необходимо осуществить поставку!
      </template>
    </Notification>

    <Notification class="mb-6" status="info">
      Мы усовершенствовали реферальную программу, воспользуйтесь первыми! <a href="#" @click.prevent="openReferralProgramInstructionsModalWindow">Читать инструкцию</a>.
    </Notification>

    <hr class="mb-6">

    <p class="mb-2">
      Полезные ссылки:
    </p>
    <ul class="dashed mb-0">
      <li v-for="{ name, url } in usefulLinks" :key="url">
        <a :href="url" target="_blank">{{ name }}</a>.
      </li>
    </ul>

    <template v-if="authStore.isUser">
      <hr class="my-6">

      <BasicButton
        class="mb-6"
        color="body-background"
        is-small
        title="Предложить улучшения"
        @click="openFeedbackModalWindow(`Предложенные улучшения (${title})`)"
      />

      <hr class="mb-6">

      <template v-if="getSupplierFaqs.length">
        <h3 class="mb-6">
          Часто задаваемые вопросы
        </h3>

        <div v-for="faq in getSupplierFaqs" :key="faq.id" class="mb-6">
          <details class="group">
            <summary class="flex cursor-pointer list-none items-center justify-between text-xl font-medium">
              <span>{{ faq.attributes.question }}</span>
              <span class="transition group-open:rotate-180">
                <SvgIcon name="outlined/arrow-down" />
              </span>
            </summary>
            <p class="mt-4 whitespace-pre-wrap">
              {{ faq.attributes.answer }}
            </p>
          </details>
        </div>
      </template>

      <BasicButton color="red" is-small title="Выйти" @click="signOut" />
    </template>
  </SupplierContent>
</template>
